import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../../stores/featureFlags';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  TextInput,
  NumberInput,
  Button,
  Badge,
  Alert,
  ScrollArea,
  ActionIcon,
  Collapse,
  Grid,
  Box,
  Tooltip,
  Divider,
} from '@mantine/core';
import {
  X,
  Wifi,
  WifiOff,
  Play,
  Square,
  Zap,
  Clock,
  Cpu,
  ChevronRight,
  ChevronDown,
} from 'react-feather';
import { io, Socket } from 'socket.io-client';
import { env } from '../../config/env';
import { useAuthStore } from '../../stores/auth';
// MED-872: canonical nudge wire type now lives with the shared in-session hook.
import type { SessionNudge as Nudge } from '../../hooks/useSessionNudges';

interface InferenceEvent {
  timestamp: string;
  status: 'debounced' | 'called' | 'result' | 'error';
  latencyMs?: number;
  result?: {
    type: string;
    subtype?: string;
    message?: string;
    source?: string;
  };
  windowLines?: number;
  error?: string;
}

interface TranscriptLine {
  id: string;
  speaker: string;
  text: string;
  timestamp: string;
  receivedAt: string;
  isFinal: boolean;
}

export function SessionLivePage() {
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuthStore();
  const debugMode = searchParams.get('debug') === '1';
  // MED-890 sweep: gate this MED02 Live Session Delivery spike page behind the
  // live_session_delivery flag. The page has no UI entry point but is reachable
  // by URL at /coach/session/live, so it would otherwise leak the unreleased
  // feature in production. useFeatureFlag fails closed (returns false while
  // loading), satisfying the "or while flag state is still loading" clause.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  // Session form
  const [meetingUrl, setMeetingUrl] = useState('');
  const [sessionId, setSessionId] = useState(
    searchParams.get('sessionId') || ''
  );
  const [cohortId, setCohortId] = useState(
    searchParams.get('cohortId') || 'test-cohort'
  );
  const [topicName, setTopicName] = useState('Situational Adaptability');
  const [durationMinutes, setDurationMinutes] = useState<number>(15);

  // Live state
  const [isConnected, setIsConnected] = useState(false);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [nudges, setNudges] = useState<Nudge[]>([]);
  const [transcriptLines, setTranscriptLines] = useState<TranscriptLine[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [inferenceEvents, setInferenceEvents] = useState<InferenceEvent[]>([]);
  const socketRef = useRef<Socket | null>(null);
  const transcriptViewportRef = useRef<HTMLDivElement | null>(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoLoading, setDemoLoading] = useState(false);
  const [botStatus, setBotStatus] = useState<'idle' | 'joining' | 'listening'>(
    'idle'
  );

  // New state for redesign
  const [sessionStartedAt, setSessionStartedAt] = useState<Date | null>(null);
  const [elapsedDisplay, setElapsedDisplay] = useState('00:00');
  const [debugOpen, setDebugOpen] = useState(debugMode);
  const [highlightedNudgeIds, setHighlightedNudgeIds] = useState<Set<string>>(
    new Set()
  );

  // Derived page state
  const isPreSession =
    !isSessionActive && nudges.length === 0 && transcriptLines.length === 0;
  const activeNudgeCount = nudges.filter(n => !n.dismissed).length;

  const addEvent = useCallback((msg: string) => {
    const ts = new Date().toLocaleTimeString();
    setEvents(prev => [`[${ts}] ${msg}`, ...prev].slice(0, 100));
  }, []);

  // Elapsed timer
  useEffect(() => {
    if (!sessionStartedAt) return;
    const tick = () => {
      const diff = Math.floor((Date.now() - sessionStartedAt.getTime()) / 1000);
      const mins = Math.floor(diff / 60);
      const secs = diff % 60;
      setElapsedDisplay(
        `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      );
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [sessionStartedAt]);

  // Stop the timer when session becomes inactive (freeze at final value)
  useEffect(() => {
    if (!isSessionActive && sessionStartedAt) {
      // Compute one final elapsed display and leave it frozen
      const diff = Math.floor((Date.now() - sessionStartedAt.getTime()) / 1000);
      const mins = Math.floor(diff / 60);
      const secs = diff % 60;
      setElapsedDisplay(
        `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      );
    }
  }, [isSessionActive, sessionStartedAt]);

  // Auto-scroll transcript to bottom only if user is already near the bottom
  const isNearBottomRef = useRef(true);
  useEffect(() => {
    const vp = transcriptViewportRef.current;
    if (!vp) return;
    const onScroll = () => {
      isNearBottomRef.current =
        vp.scrollHeight - vp.scrollTop - vp.clientHeight < 60;
    };
    vp.addEventListener('scroll', onScroll);
    return () => vp.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const vp = transcriptViewportRef.current;
    if (vp && isNearBottomRef.current) {
      vp.scrollTop = vp.scrollHeight;
    }
  }, [transcriptLines]);

  // Debounced session ID for Socket.IO connection (avoids reconnect on every keystroke)
  const [debouncedSessionId, setDebouncedSessionId] = useState(sessionId);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSessionId(sessionId), 500);
    return () => clearTimeout(timer);
  }, [sessionId]);

  // Socket.IO connection
  useEffect(() => {
    if (!debouncedSessionId) return;

    const socket = io(env.sessionIntelligenceUrl, {
      query: { sessionId: debouncedSessionId },
      auth: { token: accessToken },
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      setIsConnected(true);
      addEvent('Connected to session-intelligence');
    });

    socket.on('disconnect', (reason: string) => {
      setIsConnected(false);
      addEvent(`Disconnected: ${reason}`);
    });

    socket.on('connect_error', (err: Error) => {
      addEvent(`Connection error: ${err.message}`);
    });

    socket.on('session_started', (data: { sessionId: string }) => {
      // Idempotent — handleStartSession already sets these optimistically
      setIsSessionActive(true);
      setSessionStartedAt(prev => prev ?? new Date());
      setBotStatus(prev => (prev === 'idle' ? 'joining' : prev));
      addEvent(`Session started: ${data.sessionId}`);
    });

    socket.on('session_ended', (data: { sessionId: string }) => {
      setIsSessionActive(false);
      // Do NOT clear sessionStartedAt so the timer freezes at its final value
      addEvent(`Session ended: ${data.sessionId}`);
    });

    socket.on('nudge', (nudge: Nudge) => {
      setNudges(prev => [nudge, ...prev]);
      addEvent(
        `Nudge received: [${nudge.subtype}] ${nudge.message.slice(0, 60)}...`
      );

      // Highlight the new nudge for 5 seconds
      setHighlightedNudgeIds(prev => new Set(prev).add(nudge.id));
      setTimeout(() => {
        setHighlightedNudgeIds(prev => {
          const next = new Set(prev);
          next.delete(nudge.id);
          return next;
        });
      }, 30000);
    });

    socket.on(
      'transcript',
      (data: {
        speaker: string;
        text: string;
        timestamp: string;
        isFinal: boolean;
      }) => {
        setBotStatus(prev => (prev === 'joining' ? 'listening' : prev));
        setTranscriptLines(prev => {
          const last = prev.length > 0 ? prev[prev.length - 1] : null;

          // Only the very last line can be in-progress (non-final).
          // Replace it if: same speaker AND it hasn't been finalized yet.
          if (last && !last.isFinal && last.speaker === data.speaker) {
            const updated = prev.slice();
            updated[updated.length - 1] = {
              ...last,
              text: data.text,
              timestamp: data.timestamp,
              isFinal: data.isFinal,
            };
            return updated;
          }

          // If the last line is a stale partial from a *different* speaker
          // (speaker changed mid-partial), finalize it before appending.
          let base = prev;
          if (last && !last.isFinal && last.speaker !== data.speaker) {
            base = prev.slice();
            base[base.length - 1] = { ...last, isFinal: true };
          }

          // Append new line
          const newLine: TranscriptLine = {
            id: crypto.randomUUID(),
            speaker: data.speaker,
            text: data.text,
            timestamp: data.timestamp,
            receivedAt: new Date().toISOString(),
            isFinal: data.isFinal,
          };
          return [...base, newLine].slice(-200);
        });
        if (data.isFinal) {
          addEvent(`T: [${data.speaker}] ${data.text.slice(0, 50)}`);
        }
      }
    );

    socket.on('inference', (data: InferenceEvent) => {
      setInferenceEvents(prev => [data, ...prev].slice(0, 50));
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [debouncedSessionId, accessToken, addEvent]);

  const handleStartSession = async () => {
    if (!meetingUrl || !sessionId) {
      setError('Meeting URL and Session ID are required');
      return;
    }
    setError(null);
    setStarting(true);

    try {
      const res = await fetch(
        `${env.sessionIntelligenceUrl}/api/sessions/start`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            meetingUrl,
            sessionId,
            cohortId,
            topicName,
            durationMinutes,
          }),
        }
      );
      const data = await res.json();
      if (!data.success)
        throw new Error(data.error || 'Failed to start session');
      // Optimistically transition to active state — don't rely solely on socket event
      setIsSessionActive(true);
      setSessionStartedAt(new Date());
      setBotStatus('joining');
      addEvent('Session started');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
    } finally {
      setStarting(false);
    }
  };

  const handleStopSession = async () => {
    setStopping(true);
    try {
      const res = await fetch(
        `${env.sessionIntelligenceUrl}/api/sessions/stop`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ sessionId }),
        }
      );
      const data = await res.json();
      if (!data.success)
        throw new Error(data.error || 'Failed to stop session');
      addEvent('Session stop requested');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop session');
    } finally {
      setStopping(false);
    }
  };

  const handleDismissNudge = (nudgeId: string) => {
    socketRef.current?.emit('dismiss', nudgeId);
    setNudges(prev =>
      prev.map(n => (n.id === nudgeId ? { ...n, dismissed: true } : n))
    );
    addEvent(`Nudge dismissed: ${nudgeId.slice(0, 8)}`);
  };

  const handleNewSession = () => {
    setNudges([]);
    setTranscriptLines([]);
    setInferenceEvents([]);
    setEvents([]);
    setSessionStartedAt(null);
    setIsSessionActive(false);
    setBotStatus('idle');
    setError(null);
  };

  const handleDemo = async () => {
    if (!sessionId) return;
    setDemoLoading(true);
    try {
      await fetch(`${env.sessionIntelligenceUrl}/api/sessions/demo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ sessionId }),
      });
      addEvent('Demo sequence triggered');
    } catch {
      addEvent('Demo trigger failed');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleDemoPacing = () => {
    const remaining =
      durationMinutes -
      Math.floor(
        (Date.now() - (sessionStartedAt?.getTime() || Date.now())) / 60000
      );
    const nudge: Nudge = {
      id: crypto.randomUUID(),
      type: 'nudge',
      subtype: 'pacing',
      message: `You have about ${Math.max(remaining, 2)} minutes remaining but are still in Topic Exploration. The Action Design segment needs at least ${Math.ceil(durationMinutes * 0.17)} minutes. Consider transitioning to Group Coaching now to protect the closing commitment.`,
      timestamp: new Date().toISOString(),
      dismissed: false,
      pinned: false,
    };
    setNudges(prev => [nudge, ...prev]);
    setHighlightedNudgeIds(prev => new Set(prev).add(nudge.id));
    setTimeout(() => {
      setHighlightedNudgeIds(prev => {
        const next = new Set(prev);
        next.delete(nudge.id);
        return next;
      });
    }, 30000);
    addEvent('Demo pacing nudge triggered');
  };

  // MED-890 sweep: redirect away when live_session_delivery is off (or still
  // loading — useFeatureFlag fails closed). Placed after all hook calls.
  if (!liveSessionDeliveryEnabled) {
    return <Navigate to="/coach" replace />;
  }

  // ---------------------------------------------------------------------------
  // Pre-session state: show centered setup form
  // ---------------------------------------------------------------------------
  if (isPreSession) {
    return (
      <Container size="md" py="md">
        <Group justify="space-between" mb="md">
          <Title order={2}>Session Intelligence</Title>
          <Badge
            size="sm"
            color={isConnected ? 'green' : 'red'}
            leftSection={
              isConnected ? <Wifi size={14} /> : <WifiOff size={14} />
            }
          >
            {isConnected ? 'Connected' : 'Disconnected'}
          </Badge>
        </Group>

        {error && (
          <Alert
            color="red"
            mb="md"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Box maw={600} mx="auto">
          <Card withBorder p="md">
            <Title order={4} mb="sm">
              Session Setup
            </Title>
            <Stack gap="sm">
              <Group grow>
                <TextInput
                  label="Session ID"
                  placeholder="test-1"
                  value={sessionId}
                  onChange={e => setSessionId(e.currentTarget.value)}
                  disabled={isSessionActive}
                />
                <TextInput
                  label="Cohort ID"
                  placeholder="c-1"
                  value={cohortId}
                  onChange={e => setCohortId(e.currentTarget.value)}
                  disabled={isSessionActive}
                />
              </Group>
              <TextInput
                label="Meeting URL"
                placeholder="https://meet.google.com/abc-defg-hij"
                value={meetingUrl}
                onChange={e => setMeetingUrl(e.currentTarget.value)}
                disabled={isSessionActive}
              />
              <Group grow>
                <TextInput
                  label="Topic"
                  placeholder="Situational Adaptability"
                  value={topicName}
                  onChange={e => setTopicName(e.currentTarget.value)}
                  disabled={isSessionActive}
                />
                <NumberInput
                  label="Duration (min)"
                  value={durationMinutes}
                  onChange={val =>
                    setDurationMinutes(typeof val === 'number' ? val : 15)
                  }
                  min={1}
                  max={120}
                  disabled={isSessionActive}
                />
              </Group>
              <Group>
                <Button
                  leftSection={<Play size={16} />}
                  onClick={handleStartSession}
                  loading={starting}
                  disabled={isSessionActive || !sessionId || !meetingUrl}
                >
                  Start Session
                </Button>
                <Button
                  leftSection={<Cpu size={16} />}
                  color="violet"
                  variant="light"
                  disabled={!sessionId}
                  loading={demoLoading}
                  onClick={handleDemo}
                >
                  Demo Nudges
                </Button>
              </Group>
            </Stack>
          </Card>
        </Box>
      </Container>
    );
  }

  // ---------------------------------------------------------------------------
  // Active session and post-session states
  // ---------------------------------------------------------------------------
  return (
    <Container size="xl" py="md">
      {/* Status Bar */}
      <Box
        bg="gray.0"
        py="xs"
        px="md"
        style={{
          borderRadius: 8,
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <Group justify="space-between">
          <Group gap="xs">
            <Text fw={700}>{topicName}</Text>
            <Text size="xs" c="dimmed">
              #{sessionId}
            </Text>
          </Group>
          <Text ff="monospace" size="lg" fw={600}>
            {elapsedDisplay}
          </Text>
          <Group gap="sm">
            <Tooltip label={isConnected ? 'Connected' : 'Disconnected'}>
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: isConnected
                    ? 'var(--mantine-color-green-6)'
                    : 'var(--mantine-color-red-6)',
                }}
              />
            </Tooltip>
            {isSessionActive ? (
              <Button
                size="xs"
                color="red"
                variant="outline"
                leftSection={<Square size={14} />}
                onClick={handleStopSession}
                loading={stopping}
              >
                Stop
              </Button>
            ) : (
              <Button size="xs" variant="light" onClick={handleNewSession}>
                New Session
              </Button>
            )}
          </Group>
        </Group>
      </Box>

      {error && (
        <Alert
          color="red"
          mb="md"
          mt="md"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Grid gutter="md" mt="md">
        {/* Nudges Column */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <Zap size={18} />
              <Text fw={600} size="lg">
                Nudges
              </Text>
              {activeNudgeCount > 0 && (
                <Badge size="sm" color="orange">
                  {activeNudgeCount}
                </Badge>
              )}
            </Group>
            <Group gap="xs">
              <Button
                leftSection={<Clock size={14} />}
                color="blue"
                variant="light"
                size="xs"
                onClick={handleDemoPacing}
              >
                Demo Pacing
              </Button>
              <Button
                leftSection={<Cpu size={14} />}
                color="violet"
                variant="light"
                size="xs"
                disabled={!sessionId}
                loading={demoLoading}
                onClick={handleDemo}
              >
                Demo Scenario
              </Button>
            </Group>
          </Group>

          <ScrollArea h="calc(100vh - 240px)">
            <Stack gap="sm">
              {/* Session context + guide — shown when no nudges */}
              {nudges.length === 0 && (
                <Card
                  withBorder
                  p="lg"
                  bg="gray.0"
                  style={{ borderStyle: 'dashed' }}
                >
                  <Stack align="center" gap="md" py="xl">
                    <Box style={{ opacity: 0.3 }}>
                      <Zap size={48} />
                    </Box>
                    <Stack align="center" gap={4}>
                      <Text fw={600} size="md">
                        Monitoring: {topicName}
                      </Text>
                      <Text size="sm" c="dimmed" ta="center" maw={400}>
                        AI nudges will appear here when the conversation drifts
                        off-topic or the session is running behind on time.
                      </Text>
                    </Stack>
                    <Divider w="100%" />
                    <Grid w="100%" gutter="md">
                      <Grid.Col span={6}>
                        <Card withBorder p="sm" h="100%">
                          <Group gap="xs" mb={4}>
                            <Zap
                              size={14}
                              color="var(--mantine-color-orange-6)"
                            />
                            <Text size="xs" fw={600}>
                              Topic Alignment
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed">
                            Detects when conversation drifts off-topic for 4+
                            minutes and suggests a bridge back to the framework.
                          </Text>
                        </Card>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Card withBorder p="sm" h="100%">
                          <Group gap="xs" mb={4}>
                            <Clock
                              size={14}
                              color="var(--mantine-color-blue-6)"
                            />
                            <Text size="xs" fw={600}>
                              Pacing
                            </Text>
                          </Group>
                          <Text size="xs" c="dimmed">
                            Alerts when the closing action segment is at risk of
                            being skipped due to time.
                          </Text>
                        </Card>
                      </Grid.Col>
                    </Grid>
                  </Stack>
                </Card>
              )}

              {/* Nudges list */}
              {nudges.map(nudge => {
                const isPacing = nudge.subtype === 'pacing';
                const color = isPacing ? 'blue' : 'orange';
                const isHighlighted = highlightedNudgeIds.has(nudge.id);
                return (
                  <Card
                    key={nudge.id}
                    withBorder
                    p="md"
                    style={{
                      borderLeft: `4px solid var(--mantine-color-${color}-${isHighlighted ? '5' : '3'})`,
                      opacity: nudge.dismissed ? 0.5 : 1,
                      transition: 'border-color 0.5s ease, opacity 0.3s ease',
                      backgroundColor: isHighlighted
                        ? `var(--mantine-color-${color}-0)`
                        : undefined,
                    }}
                  >
                    <Group justify="space-between" mb="xs">
                      <Group gap="xs">
                        <Badge
                          size="sm"
                          color={color}
                          variant="light"
                          leftSection={
                            isPacing ? <Clock size={12} /> : <Zap size={12} />
                          }
                        >
                          {isPacing ? 'Pacing' : 'Topic Alignment'}
                        </Badge>
                        <Text size="xs" c="dimmed">
                          {new Date(nudge.timestamp).toLocaleTimeString()}
                        </Text>
                      </Group>
                      {!nudge.dismissed && (
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="gray"
                          onClick={() => handleDismissNudge(nudge.id)}
                        >
                          <X size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                    <Text size="sm" lh={1.5}>
                      {nudge.message}
                    </Text>
                    {nudge.source && (
                      <Text size="xs" c="dimmed" mt="xs" fs="italic">
                        {nudge.source}
                      </Text>
                    )}
                    {nudge.dismissed && (
                      <Text size="xs" c="dimmed" mt={4}>
                        Dismissed
                      </Text>
                    )}
                  </Card>
                );
              })}
            </Stack>
          </ScrollArea>
        </Grid.Col>

        {/* Transcript Column */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Group gap="xs" mb="sm">
            <Text fw={600} size="sm">
              Live Transcript
            </Text>
            {transcriptLines.length > 0 && (
              <Badge size="xs" color="gray">
                {transcriptLines.length} lines
              </Badge>
            )}
            {botStatus === 'joining' && (
              <Badge size="xs" color="yellow" variant="light">
                Bot joining...
              </Badge>
            )}
            {botStatus === 'listening' && (
              <Badge
                size="xs"
                color="green"
                variant="light"
                leftSection={
                  <Box
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'var(--mantine-color-green-6)',
                    }}
                  />
                }
              >
                Listening
              </Badge>
            )}
          </Group>

          {(() => {
            const last =
              transcriptLines.length > 0
                ? transcriptLines[transcriptLines.length - 1]
                : null;
            const inProgress = last && !last.isFinal ? last : null;
            const finalizedLines = inProgress
              ? transcriptLines.slice(0, -1)
              : transcriptLines;

            return (
              <Card
                withBorder
                p="sm"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'calc(100vh - 240px)',
                }}
              >
                {/* Finalized history — scrollable */}
                <ScrollArea
                  style={{ flex: 1, minHeight: 0 }}
                  viewportRef={transcriptViewportRef}
                >
                  {finalizedLines.length === 0 && !inProgress ? (
                    <Text c="dimmed" size="sm" ta="center" py="md">
                      {botStatus === 'joining'
                        ? 'Bot is joining the meeting...'
                        : botStatus === 'listening'
                          ? 'Listening — waiting for speech...'
                          : 'Waiting for transcript data...'}
                    </Text>
                  ) : (
                    <Stack gap={2}>
                      {finalizedLines.map(line => (
                        <Text key={line.id} size="sm" ff="monospace">
                          <Text span fw={600} c="blue">
                            {line.speaker}:
                          </Text>{' '}
                          {line.text}
                        </Text>
                      ))}
                    </Stack>
                  )}
                </ScrollArea>

                {/* In-progress line — pinned to bottom, outside scroll */}
                {inProgress && (
                  <Box
                    pt="xs"
                    mt="xs"
                    style={{
                      borderTop: '1px solid var(--mantine-color-gray-3)',
                      minHeight: '2em',
                    }}
                  >
                    <Text size="sm" ff="monospace" c="dimmed">
                      <Text span fw={600} c="blue">
                        {inProgress.speaker}:
                      </Text>{' '}
                      {inProgress.text}
                    </Text>
                  </Box>
                )}
              </Card>
            );
          })()}
        </Grid.Col>
      </Grid>

      {/* Debug Tools Section */}
      <Divider my="md" />
      <Button
        variant="subtle"
        color="gray"
        size="sm"
        leftSection={
          debugOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />
        }
        onClick={() => setDebugOpen(o => !o)}
      >
        Debug Tools
        {debugMode && (
          <Badge size="xs" color="violet" ml="xs">
            DEBUG
          </Badge>
        )}
      </Button>

      <Collapse in={debugOpen}>
        <Grid gutter="md" mt="sm">
          {/* LLM Activity Card */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder p="md">
              <Title order={4} mb="sm">
                <Group gap="xs">
                  <Cpu size={18} />
                  LLM Activity
                  {inferenceEvents.length > 0 && (
                    <Badge size="sm" color="violet">
                      {
                        inferenceEvents.filter(e => e.status === 'result')
                          .length
                      }{' '}
                      calls
                    </Badge>
                  )}
                </Group>
              </Title>
              <ScrollArea h={250}>
                {inferenceEvents.filter(e => e.status !== 'debounced')
                  .length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="md">
                    No LLM activity yet. Inference runs after transcript
                    arrives.
                  </Text>
                ) : (
                  <Stack gap={6}>
                    {inferenceEvents
                      .filter(e => e.status !== 'debounced')
                      .map((ev, i) => (
                        <Card
                          key={i}
                          withBorder
                          p="xs"
                          bg={
                            ev.status === 'result' &&
                            ev.result?.type === 'nudge'
                              ? 'orange.0'
                              : ev.status === 'error'
                                ? 'red.0'
                                : ev.status === 'debounced'
                                  ? 'gray.0'
                                  : undefined
                          }
                        >
                          <Group justify="space-between" gap="xs">
                            <Group gap="xs">
                              <Badge
                                size="xs"
                                color={
                                  ev.status === 'result'
                                    ? ev.result?.type === 'nudge'
                                      ? 'orange'
                                      : 'green'
                                    : ev.status === 'debounced'
                                      ? 'gray'
                                      : ev.status === 'error'
                                        ? 'red'
                                        : 'blue'
                                }
                              >
                                {ev.status === 'result'
                                  ? ev.result?.type
                                  : ev.status}
                              </Badge>
                              {ev.latencyMs != null && (
                                <Text size="xs" c="dimmed">
                                  {ev.latencyMs}ms
                                </Text>
                              )}
                              {ev.windowLines != null && (
                                <Text size="xs" c="dimmed">
                                  {ev.windowLines} lines
                                </Text>
                              )}
                            </Group>
                            <Text size="xs" c="dimmed">
                              {new Date(ev.timestamp).toLocaleTimeString()}
                            </Text>
                          </Group>
                          {ev.status === 'result' &&
                            ev.result?.type === 'nudge' && (
                              <Text size="xs" mt={4}>
                                {ev.result.message}
                              </Text>
                            )}
                          {ev.status === 'error' && ev.error && (
                            <Text size="xs" c="red" mt={4}>
                              {ev.error}
                            </Text>
                          )}
                        </Card>
                      ))}
                  </Stack>
                )}
              </ScrollArea>
            </Card>
          </Grid.Col>

          {/* Event Log Card */}
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder p="md">
              <Title order={4} mb="sm">
                Event Log
              </Title>
              <ScrollArea h={250}>
                {events.length === 0 ? (
                  <Text c="dimmed" size="sm" ta="center" py="md">
                    No events yet.
                  </Text>
                ) : (
                  <Stack gap={4}>
                    {events.map((event, i) => (
                      <Text key={i} size="xs" ff="monospace" c="dimmed">
                        {event}
                      </Text>
                    ))}
                  </Stack>
                )}
              </ScrollArea>
            </Card>
          </Grid.Col>
        </Grid>
      </Collapse>
    </Container>
  );
}
